import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';
import AppIcon, { type AppIconProps } from './AppIcon';

const inputVariants = cva(
  'input-field flex w-full items-center gap-4 rounded-[5px] border border-[var(--input-border)] bg-[var(--input-bg)] p-3 text-[var(--input-text)] transition-all duration-150 focus-within:border-[var(--input-focus-border)] focus-within:ring-1 focus-within:ring-[var(--input-focus-ring)]',
  {
    variants: {
      size: {
        default: 'h-11 text-sm',
        sm: 'h-9 text-sm',
        lg: 'h-12 text-base',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>, VariantProps<typeof inputVariants> {
  /** Optional icon name (Material Symbols) to display on the left */
  icon?: AppIconProps;
  /** Optional label/title displayed above the input */
  title?: string;
  /** Container className for the wrapper div */
  containerClassName?: string;
  /** Placeholder text color (Tailwind class) */
  placeholderColor?: string;
  /** Error message to display */
  error?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      containerClassName,
      type,
      icon,
      title,
      size,
      placeholder,
      placeholderColor = 'text-[var(--input-placeholder)]',
      error,
      ...props
    },
    ref
  ) => {
    const inputId = React.useId();

    return (
      <div className={cn('flex flex-col gap-1.5', containerClassName)}>
        {title && (
          <label htmlFor={inputId} className="input-title ml-1 text-xs font-normal text-[var(--input-title)]">
            {title}
          </label>
        )}
        <div
          className={cn(
            inputVariants({ size }),
            className,
            error && 'border-red-500 focus-within:border-red-500 focus-within:ring-red-500'
          )}
        >
          {icon && (
            <AppIcon
              source={icon.source}
              name={icon.name}
              svgContent={icon.svgContent}
              size={20}
              weight={400}
              colorStyle={icon.colorStyle || { type: 'solid', color: 'var(--input-icon)' }}
              className="shrink-0"
            />
          )}
          <input
            id={inputId}
            type={type}
            className={cn(
              'h-full w-full flex-1 bg-transparent font-[var(--font-sora)] text-inherit outline-none disabled:cursor-not-allowed disabled:opacity-50',
              `placeholder:${placeholderColor}`
            )}
            placeholder={placeholder}
            ref={ref}
            {...props}
          />
        </div>
        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input, inputVariants };
